/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useId, useMemo, useState } from "react";

import { AutocompleteHookForm } from "components";
import { useSearchAutoComplete } from "hooks";
import { useAuth } from "hooks/useAuth";
import uniqBy from "lodash/uniqBy";
import { useQuery } from "react-query";
import { checkMainLan, getOptionsSelect } from "utils";

import { ModelName } from "./types";
import { getServices } from "./utils";

interface Props {
  label?: string;
  placeholder?: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  model: ModelName;
  keyValue?: string;
  keyLabel?: string;
  keySubLabel?: string;
  exclude?: string[];
  depField?: string | number;
  useDepField?: boolean;
  filterLocal?: boolean;
}

const defaultPage = 1;
const defaultLimit = 50;

type ShortItem = {
  id: number | string;
  code: string;
  name: string;
  nativeName?: string;
};

const AutocompleteHookFormAsync = (props: Props) => {
  const {
    label,
    placeholder,
    name,
    required,
    model = "employee",
    disabled,
    keyValue = "id",
    keyLabel = "name",
    keySubLabel,
    exclude = [],
    depField,
    useDepField,
    filterLocal = false,
  } = props;
  const queryKey = useId();

  const [page, setPage] = useState(defaultPage);
  const [data, setData] = useState<ShortItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const { userAppRole, organizations, apps } = useAuth();

  const searchAutocomplete = useSearchAutoComplete<string>(undefined);
  const enableQuery = useMemo(() => {
    if (userAppRole === "Employee") {
      if (["organization", "application"]?.includes(model)) {
        return false;
      }
    }
    if (useDepField) {
      return searchAutocomplete.isFocus && !!depField;
    }
    return searchAutocomplete.isFocus;
  }, [depField, model, searchAutocomplete.isFocus, useDepField, userAppRole]);

  const keyLabelLan = useMemo(() => {
    if (keyLabel) return keyLabel;
    if (
      !keyLabel &&
      ["country", "province", "district", "ward"]?.includes(model)
    ) {
      return checkMainLan() ? "nativeName" : "name";
    }
    return "name";
  }, [keyLabel, model]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { isLoading } = useQuery({
    queryKey: [queryKey, searchAutocomplete.keyword, depField, page],
    enabled: enableQuery,
    queryFn: () =>
      getServices(model, page, searchAutocomplete.keyword, depField),
    onSuccess: (res) => {
      if (page === defaultPage) {
        setData(res?.data?.items as ShortItem[]);
      } else {
        setData((prevData) =>
          uniqBy([...prevData, ...(res?.data?.items || [])], keyValue)
        );
      }
      if (res?.data?.items?.length === defaultLimit) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    },
  });

  const options = useMemo(() => {
    let optionsFilter = data;
    if (userAppRole === "Employee") {
      if (["organization", "application"]?.includes(model)) {
        if (model === "organization") {
          optionsFilter = organizations as ShortItem[];
        }
        if (model === "application")
          optionsFilter = apps as unknown as ShortItem[];
      }
    }
    let optionFilters = [
      ...getOptionsSelect<string>({
        data: optionsFilter || [],
        keyLabel: keyLabelLan,
        keyValue,
        keySubLabel,
      }).filter((item) => !exclude?.includes(String(item.value))),
    ];
    if (filterLocal && searchAutocomplete.keyword) {
      optionFilters = optionFilters.filter((option) =>
        option.label
          .toLowerCase()
          .includes(searchAutocomplete.keyword.toLowerCase())
      );
    }
    return optionFilters;
  }, [
    data,
    userAppRole,
    keyLabelLan,
    keyValue,
    keySubLabel,
    filterLocal,
    searchAutocomplete.keyword,
    model,
    apps,
    organizations,
    exclude,
  ]);

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [hasMore, isLoading]);

  return (
    <AutocompleteHookForm
      name={name}
      label={label}
      placeholder={placeholder}
      options={options}
      onInputChange={searchAutocomplete.setKeyword}
      onFocus={searchAutocomplete.onFocus}
      required={required}
      disabled={disabled}
      loading={isLoading}
      disabledLocalSearch
      onLoadMore={handleLoadMore}
    />
  );
};

export default AutocompleteHookFormAsync;
